import React, { Fragment, useState } from 'react';
import classnames from 'classnames';
import { ViewportList } from 'react-viewport-list';

// Types
import { TableRowsProps } from './types';

// Components
import { WpButton } from '../button';
import WpIcon from '../icon/WpIcon';
import { WpCheckbox } from '../checkbox';

// Styles
import './TableRows.scss';

//
// TODO: (josh) - refactor these into their own individual components
//

const Cell = ({ row, column, baseClassName, slimRow }) => {
  if (column.showColumn === false) return null;
  const tdClasses = classnames(
    `${baseClassName}__tr__td`,
    slimRow && `${baseClassName}__tr__td--slim`,
    column?.rightAlign && `${baseClassName}__tr__td--right-align`
  );
  if (column?.component) {
    const Cell = column.component;
    return (
      <td className={tdClasses} style={{ maxWidth: column.maxWidth }}>
        <Cell row={row} selector={column.selector} />
      </td>
    );
  }

  const rowData = column?.selector && column.selector(row);
  return (
    <td className={tdClasses} key={`${row.slug}__${column.label}`} style={{ maxWidth: column.maxWidth }}>
      {rowData}
    </td>
  );
};

const ParentRowToggle = ({
  isOpen,
  handleToggleIsOpen,
  baseClassName,
  slimRow,
}: {
  isOpen: boolean;
  handleToggleIsOpen: () => void;
  baseClassName: string;
  slimRow: boolean;
}) => {
  const tdClasses = classnames(
    `${baseClassName}__tr__td`,
    `${baseClassName}__tr__td--nested`,
    slimRow && `${baseClassName}__tr__td--slim`
  );

  return (
    <td className={tdClasses} onClick={e => e.stopPropagation()}>
      <WpButton variant={isOpen ? 'primary' : 'secondary'} color="gray" size="xs" onClick={handleToggleIsOpen} square>
        <WpIcon name={isOpen ? 'angle-down' : 'angle-right'} />
      </WpButton>
    </td>
  );
};

const Row = ({
  row,
  index,
  tableId,
  nestedKey,
  baseClassName,
  expandAll,
  showBorder,
  onClick,
  handleRowClick,
  slimRow,
  hasNesting,
  columns,
  handleSelectSubItem,
  pluralItemLabel,
  singleItemLabel,
  selectable,
  selectableSelector,
  selectedRowKeys,
  handleSelectRow,
  handleUnSelectRow,
}) => {
  const key = `${tableId}__tr-${index}`;
  const classes = classnames(
    `${baseClassName}__tr`,
    showBorder && `${baseClassName}__tr--bordered`,
    onClick && `${baseClassName}__tr--pointer`
  );

  const [isOpen, setIsOpen] = useState(expandAll || false);

  const handleToggleIsOpen = () => setIsOpen(!isOpen);
  const children = nestedKey ? nestedKey(row) : [];
  const hasChildren = children.length > 0;

  const isSelected = selectable && selectableSelector && selectedRowKeys.includes(selectableSelector(row));

  const handleOnChangeCheckbox = checked => {
    if (checked) handleSelectRow(row);
    else handleUnSelectRow(row);
  };

  return (
    <Fragment key={key}>
      <tr className={classes} onClick={() => handleRowClick(row)}>
        {selectable && (
          <td className={`${baseClassName}__tr__td ${baseClassName}__tr__td--fit-content`}>
            <WpCheckbox
              id={`${key}-checkbox`}
              checked={isSelected}
              label={isSelected ? 'Unselect' : 'Select'}
              labelHidden
              onChange={handleOnChangeCheckbox}
              stopPropagation
            />
          </td>
        )}
        {hasChildren && (
          <ParentRowToggle
            isOpen={isOpen}
            handleToggleIsOpen={handleToggleIsOpen}
            baseClassName={baseClassName}
            slimRow={slimRow}
          />
        )}
        {!hasChildren && hasNesting && <td></td>}
        {columns.map((column, i) => (
          <Cell row={row} column={column} key={`${key}__td-${i}`} baseClassName={baseClassName} slimRow={slimRow} />
        ))}
      </tr>
      {isOpen &&
        children.map((child, i2) => (
          <Fragment key={`${key}__child-${i2}`}>
            <tr className={classes} onClick={() => handleRowClick(child)}>
              {selectable && (
                <td className={`${baseClassName}__tr__td ${baseClassName}__tr__td--fit-content`}>
                  <WpCheckbox
                    id={`${key}-${i2}-checkbox`}
                    checked={selectable && selectableSelector && selectedRowKeys.includes(selectableSelector(row))}
                    label={isSelected ? 'Unselect' : 'Select'}
                    labelHidden
                    stopPropagation
                  />
                </td>
              )}
              <td
                className={`${baseClassName}__tr__td ${baseClassName}__tr__td__child-nested ${baseClassName}__tr__td--nested`}
              >
                <div
                  className={`${baseClassName}__tr__td__child-nested__indicator  ${
                    0 === i2 ? `${baseClassName}__tr__td__child-nested__indicator--first` : ''
                  } ${children.length - 1 === i2 ? `${baseClassName}__tr__td__child-nested__indicator--last` : ''}`}
                >
                  <div
                    className={`${baseClassName}__tr__td__child-nested__indicator__crossbar  ${
                      children.length - 1 === i2
                        ? `${baseClassName}__tr__td__child-nested__indicator__crossbar--last`
                        : ''
                    }`}
                  />
                </div>
              </td>
              {columns.map((column, i) => (
                <Cell
                  row={child}
                  column={column}
                  key={`${key}__child-${i2}__td-${i}`}
                  baseClassName={baseClassName}
                  slimRow={slimRow}
                />
              ))}
            </tr>
            {nestedKey(child).length > 0 && (
              <tr>
                {selectable && <td></td>}
                <td
                  className={`${baseClassName}__tr__td ${baseClassName}__tr__td__child-nested  ${baseClassName}__tr__td--nested`}
                >
                  {children.length - 1 > i2 && <div className={`${baseClassName}__tr__td__child-nested__indicator`} />}
                </td>
                <td>
                  <div className={`${baseClassName}__tr__td__child-nested__but-wait-theres-more`}>
                    <div className={`${baseClassName}__tr__td__child-nested__but-wait-theres-more__indicator`} />
                    <WpButton color="gray" size="xs" onClick={() => handleSelectSubItem([child])} square slimPadding>
                      <WpIcon name="angle-right" />
                      {nestedKey(child).length} {nestedKey(child).length > 1 ? pluralItemLabel : singleItemLabel}
                    </WpButton>
                  </div>
                </td>
              </tr>
            )}
          </Fragment>
        ))}
    </Fragment>
  );
};

const TableRows = ({
  tableId,
  columns,
  data,
  striped,
  onClick,
  slimRow,
  showBorder,
  nestedKey,
  hasNesting,
  handleSelectSubItem,
  expandAll,
  singleItemLabel,
  pluralItemLabel,
  viewportRef,
  enableWindowScrolling = false,
  selectable = false,
  selectableSelector,
  selectedRowKeys,
  handleSelectRow,
  handleUnSelectRow,
}: TableRowsProps) => {
  const handleRowClick = row => {
    if (onClick) onClick(row);
  };

  const baseClassName = 'TableRows';

  const classes = classnames(baseClassName, striped && `${baseClassName}--striped`);

  return (
    <tbody className={classes}>
      {enableWindowScrolling ? (
        <ViewportList
          viewportRef={viewportRef}
          items={data}
          axis="y"
          renderSpacer={({ ref, style }) => <tr ref={ref} style={style} />}
          overscan={10}
        >
          {(item, index) => (
            <Row
              row={item}
              columns={columns}
              tableId={tableId}
              index={index}
              key={`${tableId}__tr-${index}`}
              baseClassName={baseClassName}
              slimRow={slimRow}
              handleRowClick={handleRowClick}
              handleSelectSubItem={handleSelectSubItem}
              singleItemLabel={singleItemLabel}
              pluralItemLabel={pluralItemLabel}
              expandAll={expandAll}
              hasNesting={hasNesting}
              onClick={onClick}
              showBorder={showBorder}
              nestedKey={nestedKey}
              selectable={selectable}
              selectedRowKeys={selectedRowKeys}
              handleSelectRow={handleSelectRow}
              handleUnSelectRow={handleUnSelectRow}
              selectableSelector={selectableSelector}
            />
          )}
        </ViewportList>
      ) : (
        <>
          {data.map((row, i) => (
            <Row
              row={row}
              columns={columns}
              tableId={tableId}
              index={i}
              key={`${tableId}__tr-${i}`}
              baseClassName={baseClassName}
              slimRow={slimRow}
              handleRowClick={handleRowClick}
              handleSelectSubItem={handleSelectSubItem}
              singleItemLabel={singleItemLabel}
              pluralItemLabel={pluralItemLabel}
              expandAll={expandAll}
              hasNesting={hasNesting}
              onClick={onClick}
              showBorder={showBorder}
              nestedKey={nestedKey}
              selectable={selectable}
              selectedRowKeys={selectedRowKeys}
              handleSelectRow={handleSelectRow}
              handleUnSelectRow={handleUnSelectRow}
              selectableSelector={selectableSelector}
            />
          ))}
        </>
      )}
    </tbody>
  );
};

export default TableRows;
